import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X, AlertCircle } from 'lucide-react';

/**
 * frontend/src/components/Toast.jsx
 *
 * Replaces window.alert(). Three reasons:
 *  - alert() is a browser chrome box that says "localhost:3000 says", which
 *    looks like the page is broken rather than like the app talking
 *  - it blocks the whole tab until dismissed
 *  - it can only report failure, so success was silent and the user was left
 *    guessing whether anything happened
 *
 * Setup — wrap the app once in App.jsx:
 *   <ToastProvider><Routes>…</Routes></ToastProvider>
 *
 * Use anywhere:
 *   const toast = useToast();
 *   toast.success('Activity created');
 *   toast.error(apiError(e, 'Could not save the activity'));
 */

const ToastContext = createContext(null);
const ConfirmContext = createContext(null);

const STYLES = {
  success: { bg:'rgba(92,195,188,.12)',  border:'#5CC3BC', fg:'#2f8a84', Icon: CheckCircle2 },
  error:   { bg:'rgba(239,68,68,.12)',   border:'#ef4444', fg:'#b91c1c', Icon: XCircle },
  warning: { bg:'rgba(217,119,6,.12)',   border:'#d97706', fg:'#b45309', Icon: AlertTriangle },
  info:    { bg:'rgba(46,83,159,.10)',   border:'#2E539F', fg:'#2E539F', Icon: Info },
};

/**
 * Pull the most useful message out of an axios error.
 * The backend sends { message, detail, blockers }; a bare "Server error" with
 * a detail is far more useful with the detail attached.
 */
export const apiError = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data;
  if (!d) return e?.message || fallback;
  if (d.message && d.detail && d.message !== d.detail) return `${d.message} — ${d.detail}`;
  return d.message || d.detail || fallback;
};

let idSeq = 0;
let warnedNoProvider = false;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts(t => t.filter(x => x.id !== id)), []);

  const push = useCallback((type, message, opts = {}) => {
    if (!message) return;
    const id = ++idSeq;
    // Errors stay until dismissed: they usually need reading and acting on.
    const ttl = opts.ttl ?? (type === 'error' ? 0 : 4000);

    setToasts(t => {
      // A page that fires several parallel requests produces one failure per
      // request when the server is unreachable. The Dashboard alone makes 14.
      // Show the message once and count the repeats instead of stacking them.
      const same = t.find(x => x.type === type && x.message === message);
      if (same) return t.map(x => x === same ? { ...x, count: (x.count || 1) + 1 } : x);
      // Never let a burst push the screen full either.
      const next = [...t, { id, type, message, title: opts.title, count: 1 }];
      return next.length > 4 ? next.slice(next.length - 4) : next;
    });

    if (ttl) setTimeout(() => dismiss(id), ttl);
    return id;
  }, [dismiss]);

  // Stable identity: pages put `toast` in dependency arrays, and a new object
  // each render would re-run those effects forever.
  const api = useMemo(() => ({
    success: (m, o) => push('success', m, o),
    error:   (m, o) => push('error',   m, o),
    warning: (m, o) => push('warning', m, o),
    info:    (m, o) => push('info',    m, o),
    dismiss,
  }), [push, dismiss]);

  // ── Confirm ──────────────────────────────────────────────────────────
  // window.confirm shows a browser box titled "localhost:3000 says", which
  // reads like the page is broken. This is promise-based so a call site can
  // swap `window.confirm(msg)` for `await confirm(msg)` and nothing else.
  const [ask, setAsk] = useState(null);
  const confirm = useCallback(
    (message, opts = {}) => new Promise(resolve => setAsk({ message, ...opts, resolve })),
    []);
  const answer = (ok) => { ask?.resolve(ok); setAsk(null); };

  return (
    <ToastContext.Provider value={api}>
     <ConfirmContext.Provider value={confirm}>
      {children}

      {ask && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => answer(false)} />
          <div className="relative w-full max-w-md rounded-xl shadow-2xl p-5"
            style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <div className="flex gap-3 mb-5">
              <span className="flex-shrink-0 mt-0.5"
                style={{ color: ask.danger === false ? '#2E539F' : '#ef4444' }}>
                <AlertCircle size={20}/>
              </span>
              <div>
                <p className="font-semibold mb-1" style={{ color:'var(--text)' }}>
                  {ask.title || (ask.danger === false ? 'Please confirm' : 'Are you sure?')}
                </p>
                <p className="text-sm whitespace-pre-line" style={{ color:'var(--text-secondary)' }}>
                  {ask.message}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => answer(false)} className="btn-secondary">
                {ask.cancelLabel || 'Cancel'}
              </button>
              <button onClick={() => answer(true)} autoFocus
                className={ask.danger === false ? 'btn-primary' : 'btn-danger'}>
                {ask.confirmLabel || (ask.danger === false ? 'Continue' : 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="fixed z-[200] flex flex-col gap-2 pointer-events-none"
        style={{ top: 72, right: 16, width: 'min(380px, calc(100vw - 32px))' }}>
        {toasts.map(t => <ToastItem key={t.id} toast={t} onDismiss={dismiss} />)}
      </div>
     </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
}

/**
 * Promise-based replacement for window.confirm.
 *   if (!await confirm('Delete this bug?')) return;
 *   if (!await confirm(msg, { danger:false, confirmLabel:'Copy' })) return;
 * The calling function must be async.
 */
const FALLBACK_CONFIRM = async (m) => window.confirm(m);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  // Without the provider, fall back so a call site still asks the question.
  // Stable for the same reason as NO_PROVIDER above.
  return ctx || FALLBACK_CONFIRM;
}

function ToastItem({ toast, onDismiss }) {
  const s = STYLES[toast.type] || STYLES.info;
  const { Icon } = s;
  const [leaving, setLeaving] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => { requestAnimationFrame(() => setEntered(true)); }, []);

  const close = () => { setLeaving(true); setTimeout(() => onDismiss(toast.id), 160); };

  return (
    <div role={toast.type === 'error' ? 'alert' : 'status'}
      className="pointer-events-auto rounded-xl shadow-lg flex items-start gap-3 px-4 py-3"
      style={{
        background: 'var(--surface)',
        borderLeft: `3px solid ${s.border}`,
        border: '1px solid var(--border)',
        borderLeftWidth: 3,
        borderLeftColor: s.border,
        opacity: leaving || !entered ? 0 : 1,
        transform: leaving ? 'translateX(12px)' : entered ? 'none' : 'translateX(12px)',
        transition: 'opacity .16s ease, transform .16s ease',
      }}>
      <span className="flex-shrink-0 mt-0.5" style={{ color: s.fg }}><Icon size={17} /></span>
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{toast.title}</p>
        )}
        <p className="text-sm leading-snug" style={{ color: 'var(--text)' }}>
          {toast.message}
          {toast.count > 1 && (
            <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-semibold"
              style={{ background: `${s.border}22`, color: s.fg }}>×{toast.count}</span>
          )}
        </p>
      </div>
      <button onClick={close} aria-label="Dismiss"
        className="flex-shrink-0 p-0.5 rounded" style={{ color: 'var(--text-secondary)' }}>
        <X size={14} />
      </button>
    </div>
  );
}

/**
 * The fallback used when no provider is mounted. It MUST be a single frozen
 * object created once.
 *
 * Returning a fresh object from useToast() gives every caller a new `toast`
 * identity on every render. Any component listing `toast` in a dependency
 * array then rebuilds its loader each render, the effect re-runs, state
 * changes, and the page re-renders forever — which looks like the page
 * endlessly reloading, not like a missing provider.
 */
const NO_PROVIDER = Object.freeze({
  success: (m) => console.info('[toast]', m),
  error:   (m) => window.alert(m),
  warning: (m) => window.alert(m),
  info:    (m) => console.info('[toast]', m),
  dismiss: () => {},
});

export function useToast() {
  const ctx = useContext(ToastContext);
  // Falling back to alert() means a page that forgot the provider still tells
  // the user something, instead of failing silently. But say so loudly in the
  // console — a browser alert appearing where a toast was expected always
  // means the provider is missing, and that is easy to miss otherwise.
  if (!ctx) {
    if (!warnedNoProvider) {
      warnedNoProvider = true;
      console.error(
        '[Toast] No <ToastProvider> above this component, so alerts are ' +
        'falling back to window.alert(). Wrap the app in App.jsx:\n' +
        "  import { ToastProvider } from './components/Toast';\n" +
        '  <AuthProvider><ToastProvider><Router>…</Router></ToastProvider></AuthProvider>'
      );
    }
    return NO_PROVIDER;
  }
  return ctx;
}
